import { Button } from 'components/shadcn/button';
import { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatMetadata, Role } from 'types';
import { apiUrl, fetchWithAuth } from 'utils';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Plus, MoreHorizontal, Trash2, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/shadcn/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/shadcn/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from 'components/shadcn/dialog';
import { Skeleton } from 'components/shadcn/skeleton';
import { AuthContext } from 'contexts';
import { useRTL } from 'hooks';

type ChatRow = Pick<ChatMetadata, 'id' | 'name' | 'inputTokens' | 'outputTokens' | 'totalMessages'>;

export const ChatList = () => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const dialogTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const { role } = useContext(AuthContext);
  const navigate = useNavigate();

  const columns: ColumnDef<ChatRow>[] = [
    {
      accessorKey: 'id',
      header: t('dashboard.chats.table.id'),
      cell: ({ row }) => (
        <div className="flex items-center font-mono gap-2">
          {row.getValue('id')}
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${row.getValue('id')}`)}>
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
    {
      accessorKey: 'name',
      header: () => <div>{t('dashboard.chats.table.name')}</div>,
      cell: ({ row }) => <div>{row.getValue('name')}</div>,
    },
    {
      accessorKey: 'inputTokens',
      header: () => <div className="text-right">{t('dashboard.chats.table.inputTokens')}</div>,
      cell: ({ row }) => {
        return (
          <div className="text-right font-medium tabular-nums">{row.getValue('inputTokens')}</div>
        );
      },
    },
    {
      accessorKey: 'outputTokens',
      header: () => <div className="text-right">{t('dashboard.chats.table.outputTokens')}</div>,
      cell: ({ row }) => {
        return (
          <div className="text-right font-medium tabular-nums">{row.getValue('outputTokens')}</div>
        );
      },
    },
    {
      accessorKey: 'totalMessages',
      header: () => <div className="text-right">{t('dashboard.chats.table.totalMessages')}</div>,
      cell: ({ row }) => {
        return (
          <div className="text-right font-medium tabular-nums">{row.getValue('totalMessages')}</div>
        );
      },
    },
    {
      id: 'actions',
      header: () => <div>{t('common.actions')}</div>,
      cell: ({ row }) => {
        const { id } = row.original;

        return (
          <div>
            <DropdownMenu dir={isRTL ? 'rtl' : 'ltr'}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">{t('aria.openMenu')}</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel hidden>{t('common.actions')}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate(`/${id}/conversations`)}>
                  {t('dashboard.chats.viewConversations')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/${id}/edit`)}>
                  {t('common.edit')}
                </DropdownMenuItem>
                {role === Role.ADMIN && (
                  <>
                    <DropdownMenuItem onClick={() => onCreateNewChat(id)}>
                      {t('dashboard.chats.duplicate')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/cost-summary/${id}`)}>
                      {t('dashboard.chats.costSummary')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDeleteClick(id)}
                      className="text-destructive focus:bg-destructive focus:text-destructive-foreground flex justify-between"
                    >
                      {t('common.delete')}
                      <Trash2 className="h-4 w-4 ms-2" />
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchChats = async () => {
      try {
        setIsLoading(true);
        const response = await fetchWithAuth(`${apiUrl}/chat-metadata`);
        const jsonData = await response.json();
        setChats(jsonData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChats();
  }, []);

  const onCreateNewChat = (chatId?: string) => {
    let url = '/create';
    if (chatId) {
      url += `?duplicateChatId=${chatId}`;
    }
    navigate(url);
  };

  const handleDeleteClick = (chatId: string) => {
    setSelectedChatId(chatId);
    if (dialogTriggerRef.current) {
      dialogTriggerRef.current.click();
    }
  };

  const handleClose = () => {
    document.getElementById('close-dialog-button')?.click(); // TODO: Find a better way to close the dialog
  };

  const handleDeleteConfirm = async () => {
    if (selectedChatId) {
      try {
        await fetchWithAuth(`${apiUrl}/chat-metadata/${selectedChatId}`, {
          method: 'DELETE',
        });
        setChats(chats.filter((chat) => chat.id !== selectedChatId));
        handleClose();
      } catch (error) {
        console.error('Error deleting chat:', error);
      }
    }
  };

  const table = useReactTable({
    data: chats,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageSize: 100,
        pageIndex: 0,
      },
    },
  });

  return (
    <div className="mx-auto py-6 px-4 lg:px-6 lg:max-w-5xl grow self-start box-border w-full animate-in fade-in duration-500">
      <div className="flex flex-row mb-10 items-center justify-between animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-backwards delay-0">
        <h1 className="text-4xl font-black text-primary tracking-tight text-balance">
          {t('dashboard.chats.title')}
        </h1>
        {role === Role.ADMIN && (
          <Button color="success" onClick={() => onCreateNewChat()}>
            <Plus className="me-2 h-4 w-4" /> {t('dashboard.chats.createNewChat')}
          </Button>
        )}
      </div>
      <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-backwards delay-100">
        <div className="rounded-md border shadow-sm bg-card transition-[box-shadow] duration-200 hover:shadow-md">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 20 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-8 w-8 rounded-md" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {t('dashboard.chats.noResults')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <Dialog>
        <DialogTrigger asChild ref={dialogTriggerRef} style={{ display: 'none' }}>
          <Button />
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard.chats.deleteDialog.title')}</DialogTitle>
            <DialogDescription>{t('dashboard.chats.deleteDialog.description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} autoFocus>
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
